import React, { useState, useEffect, useCallback } from "react";
import { ResourceTypes } from "../../services/app.service";
import {
  ContentResponse,
  ResourceResponse,
} from "../../services/api.response.service";
import CustomAudioPlayer from "../CustomAudioPlayer";
import AutoplaySwitch from "./AutoplaySwitch";
import { getCompletedListenCount } from "../../utils/contentProgress";

interface CourseContentListenProps {
  content: ContentResponse;
}

function CourseContentListen({ content }: CourseContentListenProps) {
  const AUTOPLAY_LISTEN_KEY = "ecampus_autoplay_listen";

  const [playingResourceId, setPlayingResourceId] = useState<number | null>(
    null,
  );
  const [autoplay, setAutoplayState] = useState(() => {
    try {
      return localStorage.getItem(AUTOPLAY_LISTEN_KEY) === "true";
    } catch {
      return false;
    }
  });
  const setAutoplay = useCallback((value: boolean) => {
    setAutoplayState(value);
    try {
      localStorage.setItem(AUTOPLAY_LISTEN_KEY, value ? "true" : "false");
    } catch {
      /* ignore */
    }
  }, []);
  const [nextAutoPlayId, setNextAutoPlayId] = useState<number | null>(null);

  const audioResources = content.content_resources.filter(
    (item) => item.type === ResourceTypes.AUDIO,
  ) as ResourceResponse[];

  const completedListenCount = getCompletedListenCount(content);

  const handleTrackEnded = useCallback(
    (endedResourceId: number) => {
      if (!autoplay) return;
      const currentIndex = audioResources.findIndex(
        (r) => r.id === endedResourceId,
      );
      if (currentIndex < 0 || currentIndex >= audioResources.length - 1) return;
      const next = audioResources[currentIndex + 1];
      setPlayingResourceId(next.id);
      setNextAutoPlayId(next.id);
    },
    [autoplay, audioResources],
  );

  useEffect(() => {
    if (nextAutoPlayId == null) return;
    const id = setTimeout(() => setNextAutoPlayId(null), 150);
    return () => clearTimeout(id);
  }, [nextAutoPlayId]);

  // When content loads and autoplay is on, start first track
  useEffect(() => {
    if (!autoplay || audioResources.length === 0) return;
    const first = audioResources[0];
    setPlayingResourceId(first.id);
    setNextAutoPlayId(first.id);
  }, [content.id, autoplay]);

  if (audioResources.length === 0) {
    return null;
  }

  return (
    <div
      className="d-flex flex-column w-100"
      style={{
        paddingTop: "12px",
        gap: 0,
      }}
    >
      <AutoplaySwitch
        id="listen-autoplay"
        checked={autoplay}
        onChange={setAutoplay}
        label={autoplay ? "Autoplay is on" : "Autoplay is off"}
        style={{ paddingLeft: "4px" }}
      />
      {audioResources.map((item, index) => (
        <CustomAudioPlayer
          key={item.id}
          title={`${index + 1}. ${item.description || item.name || content.title}`}
          src={item.url}
          contentId={item.content_id}
          resourceId={item.id}
          isCompleted={false}
          isActive={playingResourceId === item.id}
          onPlayRequest={() => setPlayingResourceId(item.id)}
          onEnded={() => handleTrackEnded(item.id)}
          autoPlay={nextAutoPlayId === item.id}
        />
      ))}
    </div>
  );
}

export default CourseContentListen;
